<script setup>
import { ref } from 'vue';
import { API } from '@/services/index';
import { useQueryClient, useMutation } from '@tanstack/vue-query';
import { useUserStore } from '@/stores/user.store.js';
import { useSettingsStore } from '@/stores/settings.store.js';
import { useAccountStore } from '@/stores/account.store.js';
import { useWorkspaceStore } from '@/stores/workspace.store.js';
import { ADD_FEATURE_ROLE_TO_ACCOUNT, UPDATE_USER_ROLE_IN_ACCOUNT } from '@/services/account/mutations/account';
import partnrUpShortLogo from '@/assets/logo/PartnrUP_P_logo.png';

const formRules = {
  users: (value) => value.length > 0 || 'Team members field is required.'
};

const props = defineProps({
  isDisabled: {
    type: Boolean,
    default: false
  },
  teamMembers: {
    type: Array,
    default: () => [],
    required: true
  }
  // accountRoles: {
  //   type: Array,
  //   default: () => [],
  //   required: true
  // }
});

const queryClient = useQueryClient();
const dialog = ref(false);
const activeAccount = computed(() => useSettingsStore().activeAccount);
const isPermissionsLoading = computed(() => useUserStore().isPermissionsLoading);
const isFormValid = ref(false);
const user_ids = ref([]);
const role = ref('Accountant');
const selectedAllTeamMembers = computed(() => user_ids.value.length === props.teamMembers.length);
const selectedSomeTeamMembers = computed(() => user_ids.value.length > 0);
const isSubmitLoading = ref(false);

const formmattedTeamMembers = computed(() => {
  return props.teamMembers.map((item) => {
    if (item.user) {
      const name = item.user.first_name ? `${item.user.first_name} ${item.user.last_name}` : item.user.username ?? item.user.email;
      return {
        id: Number(item.user.id),
        avatar: item.user.avatar,
        // name: item.user.username ?? item.user.first_name ? `${item.user.first_name} ${item.user.last_name}` : item.user.email,
        name: name,
        email: item.user.email
      };
    }
  });
});

const { isPending, isError, error, isSuccess, mutate } = useMutation({
  mutationFn: async (payload) => {
    return await API.sendQuery('/graphql/account', ADD_FEATURE_ROLE_TO_ACCOUNT, payload);
  },
  onSuccess: async (data) => {
    if (data.data.data.addFeatureRole) {
      isSubmitLoading.value = false;
      await queryClient.invalidateQueries({ queryKey: ['loadAccountTeamMembers'] });
    }

    if (data.data.errors) {
      console.log(data.data.errors);
      isSubmitLoading.value = false;
    }
  },
  onError: (error) => {
    console.log('error', error);
    isSubmitLoading.value = false;
  }
});

const onSubmitHandler = async () => {
  try {
    for (const user of user_ids.value) {
      isSubmitLoading.value = true;
      const payload = {
        account_id: Number(activeAccount.value.id),
        user_id: user.id,
        role: 'Accountant'
      };
      // console.log('payload', payload);
      await mutate(payload);
    }
  } catch (error) {
    console.log('error', error);
  } finally {
    // isSubmitLoading.value = false;
    dialog.value = false;
    clearForm();
    // await queryClient.invalidateQueries({ queryKey: ['loadAccountTeamMembers'] });
  }
};

const selectAllTeamMembersHandle = () => {
  if (selectedAllTeamMembers.value) {
    user_ids.value = [];
  } else {
    user_ids.value = formmattedTeamMembers.value.map((user) => user.id);
  }
};

const clearForm = () => {
  user_ids.value = [];
};
</script>

<template>
  <v-btn
    :disabled="isPermissionsLoading || isDisabled"
    @click="dialog = true"
    class="tw-text-[0.79rem] tw-capitalize"
    color="primary-blue"
    prepend-icon="mdi-plus"
  >
    Give Financial Access
  </v-btn>
  <v-dialog v-model="dialog" @update:modelValue="clearForm" width="auto" attach="#app" :persistent="isSubmitLoading">
    <v-card class="tw-rounded-xl" max-width="702" width="702" :loading="isPending">
      <div class="tw-px-5 tw-pb-5">
        <h2 class="tw-my-3 tw-text-center tw-text-[0.79rem] tw-text-xl tw-font-bold tw-capitalize tw-text-primary-blue">Give Financial Access</h2>
        <div class="tw-w-full tw-rounded-xl tw-bg-[#f7f7f7] tw-p-3 tw-pb-10">
          <v-form ref="inviteForm" v-model="isFormValid" @submit.prevent="onSubmitHandler">
            <div class="tw-flex tw-w-full tw-items-center tw-justify-between tw-space-x-3">
              <div class="tw-flex tw-w-full">
                <v-select
                  width="300"
                  v-model="user_ids"
                  :items="formmattedTeamMembers"
                  return-object
                  placeholder="Select Team Member"
                  item-title="name"
                  item-value="id"
                  color="primary-blue"
                  variant="solo"
                  density="compact"
                  hide-details="auto"
                  :disabled="isPending"
                  :rules="[formRules.users]"
                  multiple
                  closable-chips
                >
                  <template v-slot:prepend-item>
                    <v-list-item @click="selectAllTeamMembersHandle" title="Select All">
                      <template v-slot:prepend>
                        <v-checkbox-btn
                          :color="selectedSomeTeamMembers ? 'indigo-darken-4' : undefined"
                          :indeterminate="selectedSomeTeamMembers && !selectedAllTeamMembers"
                          :model-value="selectedAllTeamMembers"
                        ></v-checkbox-btn>
                      </template>
                    </v-list-item>
                  </template>

                  <template v-slot:selection="{ item, index }">
                    <v-chip v-if="index < 2">
                      <v-avatar v-if="item.raw.image" size="16">
                        <v-img sizes="16" contain :src="item.raw.avatar" :alt="`${item.raw.avatar} image`"></v-img>
                      </v-avatar>
                      <v-avatar v-else :rounded="false" size="16">
                        <v-img sizes="16" contain :src="partnrUpShortLogo" alt="default no-logo"></v-img>
                      </v-avatar>
                      <span style="font-size: 0.7rem; font-weight: 500; margin-left: 7px">{{ item.raw.name }}</span>
                    </v-chip>
                    <span v-if="index === 2" class="text-grey text-caption align-self-center"> (+{{ user_ids.length - 2 }} team members) </span>
                  </template>
                </v-select>
              </div>
              <div class="tw-flex tw-w-[180px]">
                <v-select
                  style="height: 40px !important"
                  width="300"
                  v-model="role"
                  item-title="text"
                  item-value="value"
                  :items="[{ value: 'Accountant', text: 'Accountant' }]"
                  placeholder="Select role"
                  color="primary-blue"
                  variant="solo"
                  density="compact"
                  hide-details
                  :disabled="isPending"
                ></v-select>
              </div>
              <div class="tw-flex tw-w-full tw-justify-end">
                <v-btn
                  type="submit"
                  :loading="isPending"
                  :disabled="isPending || !isFormValid"
                  class="tw-capitalize"
                  width="141"
                  color="primary-blue"
                >
                  Done
                </v-btn>
              </div>
            </div>
          </v-form>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<style scoped lang="css"></style>
